export const getSeasonSnapshot = async (season, walletAddress, setSnapshotFrom, setSnapshotTo, setProgress) => {
  if (!season || !season.snapshot_files) return [];  // Return empty array instead of undefined

  // Sort snapshots by created_at date
  const sortedSnapshots = [...season.snapshot_files].sort((a, b) =>
    new Date(a.created_at) - new Date(b.created_at)
  );

  // Get oldest and latest snapshots
  const oldestSnapshot = sortedSnapshots[0];
  const latestSnapshot = sortedSnapshots[sortedSnapshots.length - 1];

  const oldestSnapshotData = await fetchSnapshot(season.id, oldestSnapshot.name);
  const latestSnapshotData = await fetchSnapshot(season.id, latestSnapshot.name);

  setSnapshotFrom(oldestSnapshotData);
  setSnapshotTo(latestSnapshotData);

  if (oldestSnapshotData && latestSnapshotData) {
    // Filter criteria and keep them as separate objects in the array
    const criteria = season.seasons_airdrop_criterias;
    const filteredCriteria = Object.entries(criteria)
      .filter(([key, value]) =>
        ![
          'created_at',
          'description',
          'name',
          'season_id',
          'updated_at',
          'id'
        ].includes(key) &&
        value !== 0 &&
        typeof value === 'number'
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    // console.log("filtered criteria:", filteredCriteria); // Debug log

    const diff = await diffSnapshot(walletAddress, [filteredCriteria], oldestSnapshotData, latestSnapshotData, setProgress);
    return diff;
  }

  return [];  // Return empty array as fallback
};

export const fetchSnapshot = async (seasonId, fileName) => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/seasons/snapshots/season_${seasonId}/${fileName}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching the season snapshot:', error);
  }
};

export const diffSnapshot = async (walletAddress, criteria, snapshotFrom, snapshotTo, setProgress) => {
  // Get the differences between snapshots by comparing each wallet
  const CHUNK_SIZE = 100;
  const results = [];

  // Process chunks with progress updates
  for (let i = 0; i < snapshotTo.length; i += CHUNK_SIZE) {
    const chunk = snapshotTo.slice(i, i + CHUNK_SIZE);

    // Process each item in the chunk
    const chunkResults = chunk.map(newItem => {
      const oldItem = snapshotFrom.find(item => item.wallet === newItem.wallet);
      if (!oldItem) return null;

      const diffItem = {
        user_id: newItem.user_id,
        wallet: newItem.wallet,
        wallet_lc: newItem.wallet_lc,
        name: newItem.name,
        mxs: (newItem?.mxs || 0) - (oldItem?.mxs || 0),
        gold: (newItem?.gold || 0) - (oldItem?.gold || 0),
        ruby: (newItem?.ruby || 0) - (oldItem?.ruby || 0),
        sapphire: (newItem?.sapphire || 0) - (oldItem?.sapphire || 0),
        level_count: (newItem?.level_count || 0) - (oldItem?.level_count || 0),
        missions_count: (newItem?.missions_count || 0) - (oldItem?.missions_count || 0),
        login_count: (newItem?.login_count || 0) - (oldItem?.login_count || 0),
        ads_count: (newItem?.ads_count || 0) - (oldItem?.ads_count || 0),
        user_referral_count: (newItem?.user_referral_count || 0) - (oldItem?.user_referral_count || 0),
        products_referral: (newItem?.products_referral || 0) - (oldItem?.products_referral || 0),
        node_referral_count: (newItem?.node_referral_count || 0) - (oldItem?.node_referral_count || 0),
        nft_count: (newItem?.nft_count || 0) - (oldItem?.nft_count || 0),
        nft_power: (newItem?.nft_power || 0) - (oldItem?.nft_power || 0),
        xb: (newItem?.xb || 0) - (oldItem?.xb || 0)
      };

      // Check if any metric is negative
      const hasNegativeMetric = Object.entries(diffItem)
        .filter(([key]) => !['user_id', 'wallet', 'wallet_lc', 'name'].includes(key))
        .some(([_, value]) => value < 0);

      // Check if the diffItem meets the criteria
      const meetsCriteria = criteria.every(criteriaObj => {
        const results = Object.entries(criteriaObj).map(([key, requiredValue]) => {
          const actualValue = diffItem[key];
          const meets = actualValue >= requiredValue;
          return meets;
        });
        return results.every(result => result === true);
      });

      // return (!hasNegativeMetric && meetsCriteria) ? diffItem : null; // with filteration from criteria
      return !hasNegativeMetric ? diffItem : null; // no filteration from criteria
    }).filter(item => item !== null); // Filter null items

    // Add valid results from this chunk
    results.push(...chunkResults);

    // Update progress
    const progress = Math.min(Math.round((i + CHUNK_SIZE) / snapshotTo.length * 100), 100);
    setProgress(progress);

    // Small delay for UI update
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  // Sort final results by MXS
  const diff = results.sort((a, b) => b.mxs - a.mxs);
  // console.log(diff)

  // Find specific user in both snapshots
  const userFromOld = walletAddress ? snapshotFrom.find(item => item.wallet === walletAddress) : null;
  const userFromNew = walletAddress ? snapshotTo.find(item => item.wallet === walletAddress) : null;

  // Calculate differences for user
  const differences = userFromNew ? {
    mxs: (userFromNew?.mxs || 0) - (userFromOld?.mxs || 0),
    gold: (userFromNew?.gold || 0) - (userFromOld?.gold || 0),
    ruby: (userFromNew?.ruby || 0) - (userFromOld?.ruby || 0),
    sapphire: (userFromNew?.sapphire || 0) - (userFromOld?.sapphire || 0),
    level_count: (userFromNew?.level_count || 0) - (userFromOld?.level_count || 0),
    missions_count: (userFromNew?.missions_count || 0) - (userFromOld?.missions_count || 0),
    login_count: (userFromNew?.login_count || 0) - (userFromOld?.login_count || 0),
    ads_count: (userFromNew?.ads_count || 0) - (userFromOld?.ads_count || 0),
    user_referral_count: (userFromNew?.user_referral_count || 0) - (userFromOld?.user_referral_count || 0),
    products_referral: (userFromNew?.products_referral || 0) - (userFromOld?.products_referral || 0),
    node_referral_count: (userFromNew?.node_referral_count || 0) - (userFromOld?.node_referral_count || 0),
    nft_count: (userFromNew?.nft_count || 0) - (userFromOld?.nft_count || 0),
    nft_power: (userFromNew?.nft_power || 0) - (userFromOld?.nft_power || 0),
    xb: (userFromNew?.xb || 0) - (userFromOld?.xb || 0)
  } : null;

  // Calculate the rank based on the position in the sorted diff array
  const currentUserRank = diff.findIndex(item => item.wallet === walletAddress) + 1 || null;

  return {
    diff,
    CurrentUser: userFromNew ? {
      user: userFromNew.name,
      userComparison: {
        old: userFromOld,
        new: userFromNew
      },
      userResults: {
        rank: currentUserRank,  // Include calculated rank
        ...differences
      }
    } : null
  };
};
